import { isBuildOlderThan } from "@esi/shared";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";
import { hasUnsavedWork } from "./session-ended.ts";

/**
 * Open tabs pick up a new build (openspec change `client-build-refresh`). Every API response
 * carries the server's build in `x-esi-build-id`; one strictly newer than this tab's is a
 * candidate. Before acting on it, `/index.html` says which build a fresh load would get (its
 * `esi-build-id` meta): only a newer one is confirmed, so a canary ahead of the page or an
 * invalidation that has not landed never reloads into the same build. A confirmed update
 * reloads at a safe point (a route change, the tab becoming visible) with nothing held, at most
 * once per target build in this tab session; otherwise a toast offers the reload.
 */

// Replaced at build time (vite.config.ts `define`).
export const BUILD_ID = String(import.meta.env.ESI_BUILD_ID);

/** The target build of this tab's last automatic reload (sessionStorage). */
export const RELOADED_FOR_KEY = "esi:reloaded-for-build";
export const INDEX_PATH = "/index.html";
/**
 * An unconfirmed candidate is checked again at most this often (the session check's cadence),
 * backing off to `MAX_RECHECK_MS` while it stays unconfirmed (a long canary ahead of the index).
 */
export const RECHECK_MS = 30_000;
export const MAX_RECHECK_MS = 120_000;
/** How long a reload waits at most for the requests in flight to settle (then it stays held). */
export const SETTLE_MS = 10_000;

/** The build a page's `index.html` carries (`<meta name="esi-build-id" content="…">`). */
export function readIndexBuildId(html: string): string | null {
  const meta = new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector('meta[name="esi-build-id"]');
  return meta?.getAttribute("content")?.trim() || null;
}

/** What leaves the tab: replaceable in tests. */
export const buildRefreshIo = {
  reload: () => window.location.reload(),
  fetchIndex: (): Promise<Response> =>
    fetch(INDEX_PATH, {
      cache: "no-cache",
      credentials: "same-origin",
      signal: AbortSignal.timeout(10_000),
    }),
  storage: (): Storage => window.sessionStorage,
  now: () => Date.now(),
};

export interface BuildRefreshState {
  /** The build a reload would bring (confirmed, or the server's when required); null: none. */
  target: string | null;
  /** A `client_outdated` answer: calls may fail until the reload. */
  required: boolean;
  /** The toast is due (a target the person has not dismissed). */
  toast: boolean;
}

const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

let own = BUILD_ID;
/** The newest server build seen that is newer than `own`. */
let candidate: string | null = null;
/** The newest build `/index.html` was seen to carry that is newer than `own`. */
let confirmed: string | null = null;
let required = false;
let dismissed: string | null = null;
/** The target this tab already reloaded for by itself (or would, storage being unusable). */
let reloadedFor: string | null = null;
let storageUsable = true;
let probing: Promise<void> | null = null;
/** A newer candidate arrived while a check was in flight: check again once it ends. */
let probeAgain = false;
let probedAt = Number.NEGATIVE_INFINITY;
let recheckMs = RECHECK_MS;
/** What the last answered check said `/index.html` carries (null: no answer yet). */
let lastIndexBuild: string | null = null;
/** The path of the current route, to tell a route change from a query change. */
let routePath = "";
let state: BuildRefreshState = { target: null, required: false, toast: false };
/** Bumped by every start: a check or attempt of an earlier store changes nothing in this one. */
let epoch = 0;
/** Bumped by every cancel (and so every start): an attempt begun before one never reloads. */
let attempts = 0;

/** Index checks and reload attempts under way, joined by {@link whenBuildRefreshIdle}. */
const pending = new Set<Promise<unknown>>();
function track<T>(work: Promise<T>): Promise<T> {
  pending.add(work);
  // `finally`, not a rejection handler: a failure still surfaces as an unhandled rejection.
  void work.finally(() => {
    pending.delete(work);
  });
  return work;
}
/** Ends a reload attempt that waits (a timer, a settle wait) without reloading. */
const cancels = new Set<() => void>();

/** Resolves once no index check or reload attempt is under way (tests join them with it). */
export async function whenBuildRefreshIdle(): Promise<void> {
  while (pending.size > 0) {
    await Promise.allSettled([...pending]);
  }
}

/**
 * Ends every reload attempt that waits, without reloading: its timers are cleared, so none
 * outlives the app (`BuildRefresh` unmounting) or the store (a new start).
 */
export function cancelPendingReloads(): void {
  attempts++;
  for (const cancel of [...cancels]) {
    cancel();
  }
}

function notify(): void {
  const target = confirmed ?? (required ? (candidate ?? "required") : null);
  const next = { target, required, toast: target !== null && dismissed !== target };
  if (
    next.target !== state.target ||
    next.required !== state.required ||
    next.toast !== state.toast
  ) {
    state = next;
    for (const listener of listeners) {
      listener();
    }
  }
}

const newer = (a: string | null, b: string) => a === null || isBuildOlderThan(a, b);

/**
 * Starts this tab's store (module load, or a test with its own build). A stored target newer than
 * the tab's own build means the reload for it did not take (a stale cache): the toast stays, and
 * no automatic reload happens for that build again.
 */
export function startBuildRefresh(ownBuild: string = BUILD_ID): void {
  cancelPendingReloads();
  epoch++;
  own = ownBuild;
  candidate = null;
  confirmed = null;
  required = false;
  dismissed = null;
  reloadedFor = null;
  storageUsable = true;
  probing = null;
  probeAgain = false;
  probedAt = Number.NEGATIVE_INFINITY;
  recheckMs = RECHECK_MS;
  lastIndexBuild = null;
  routePath = typeof window === "undefined" ? "" : window.location.pathname;
  let stale: string | null = null;
  try {
    const stored = buildRefreshIo.storage().getItem(RELOADED_FOR_KEY);
    if (stored && isBuildOlderThan(own, stored)) {
      stale = stored;
      reloadedFor = stored;
      candidate = stored;
      confirmed = stored;
    }
  } catch {
    // No storage (a private window, a blocked origin): never reload by itself, only offer it.
    storageUsable = false;
  }
  state = { target: null, required: false, toast: false };
  notify();
  if (stale !== null) {
    void track(verifyStale(stale));
  }
}

/**
 * The target of a reload that did not take is checked once: if the index no longer carries a
 * newer build (a canary rolled back), there is nothing to offer and the toast goes.
 */
async function verifyStale(stale: string): Promise<void> {
  const at = epoch;
  await probe(true);
  if (
    at === epoch &&
    confirmed === stale &&
    lastIndexBuild !== null &&
    !isBuildOlderThan(own, lastIndexBuild)
  ) {
    candidate = null;
    confirmed = null;
    notify();
  }
}

/**
 * Asks `/index.html` which build a fresh load would get; one check at a time. A newer candidate
 * that arrives during a check gets one more check after it (the first may predate its index).
 */
function probe(force = false): Promise<void> {
  if (probing) {
    probeAgain ||= force;
    return probing;
  }
  if (!force && buildRefreshIo.now() - probedAt < recheckMs) {
    return Promise.resolve();
  }
  recheckMs = force ? RECHECK_MS : Math.min(recheckMs * 2, MAX_RECHECK_MS);
  const at = epoch;
  const since = attempts;
  probing = track(
    (async () => {
      try {
        do {
          probeAgain = false;
          probedAt = buildRefreshIo.now();
          await checkIndex(at, since);
        } while (probeAgain && at === epoch);
      } finally {
        // A new start has its own checks.
        if (at === epoch) {
          probing = null;
        }
      }
    })(),
  );
  return probing;
}

async function checkIndex(at: number, since: number): Promise<void> {
  try {
    const res = await buildRefreshIo.fetchIndex();
    const id = res.ok ? readIndexBuildId(await res.text()) : null;
    if (at !== epoch) {
      return;
    }
    if (res.ok) {
      lastIndexBuild = id ?? "";
    }
    if (id && isBuildOlderThan(own, id) && newer(confirmed, id)) {
      confirmed = id;
      notify();
      // Calls already fail (`client_outdated`): a confirmation that lands later reloads too.
      if (required) {
        void reloadWhenSettled("outdated", since);
      }
    }
  } catch {
    // Offline or slow: the next observation or visibility change checks again.
  }
}

/** Checks the index unless a confirmed build already covers the candidate. */
function confirmCandidate(force = false): Promise<void> {
  if (candidate === null || (confirmed !== null && !isBuildOlderThan(confirmed, candidate))) {
    return Promise.resolve();
  }
  return probe(force);
}

/**
 * A server build from a response header. Only a strictly newer, orderable one counts (equal,
 * older and unknown IDs such as `dev` or a canary's previous revision never do).
 */
export function observeBuild(serverId: string | null | undefined): Promise<void> {
  if (!serverId || !isBuildOlderThan(own, serverId)) {
    return Promise.resolve();
  }
  if (newer(candidate, serverId)) {
    candidate = serverId;
    notify();
    // A newer candidate is checked at once, whatever the last check said.
    return confirmCandidate(true);
  }
  return confirmCandidate();
}

/**
 * A `409 client_outdated`: the reload is required. It still waits for nothing held, and happens
 * only into a confirmed newer build (a misconfigured minimum leaves just the toast).
 */
export function markOutdated(serverId: string | null | undefined): Promise<void> {
  const since = attempts;
  if (!required) {
    required = true;
    notify();
  }
  return track(
    (async () => {
      await observeBuild(serverId);
      if (since !== attempts) {
        return;
      }
      // Calls fail until the reload: check the index again now, past the back-off.
      await confirmCandidate(true);
      await reloadWhenSettled("outdated", since);
    })(),
  );
}

/** A lazy chunk failed to load (`vite:preloadError`): a deploy may have replaced it. */
export function markChunkError(): Promise<void> {
  const since = attempts;
  return track(
    (async () => {
      // Unconfirmed, or a target this tab already reloaded for: a newer deploy may be behind it.
      if (confirmed === null || confirmed === reloadedFor || heldByWork()) {
        await probe(true);
      }
      await reloadWhenSettled("chunk", since);
    })(),
  );
}

/** The toast was dismissed: hidden for this target in this tab, the safe-point reload stays. */
export function dismissBuildRefresh(target: string): void {
  if (dismissed !== target) {
    dismissed = target;
    notify();
  }
}

/** The person pressed Reload: past every hold and the loop guard, since they chose it. */
export function reloadNow(): void {
  buildRefreshIo.reload();
}

// Holds: work an automatic reload would lose.
const holds = new Set<symbol>();
const holdChecks = new Set<() => boolean>();

/** Holds automatic reloads until the returned release is called. */
export function holdReload(): () => void {
  const token = Symbol("reload-hold");
  holds.add(token);
  return () => {
    holds.delete(token);
  };
}

/** A hold asked at attempt time (state outside React, such as the chat cache). */
export function addReloadHoldCheck(check: () => boolean): () => void {
  holdChecks.add(check);
  return () => {
    holdChecks.delete(check);
  };
}

/** Holds automatic reloads while `active` (released on unmount). */
export function useReloadHold(active: boolean): void {
  useEffect(() => (active ? holdReload() : undefined), [active]);
}

/**
 * Something in flight that a reload would abort: its response may carry a rotated session
 * cookie, and losing that one signed the person out (#300).
 */
export interface InFlightSource {
  busy(): boolean;
  /** Called on every change that may end being busy. */
  subscribe(listener: () => void): () => void;
}
const inFlightSources = new Map<InFlightSource, () => void>();
/** Waiters for nothing in flight: told of every change of every source, present or later. */
const inFlightListeners = new Set<() => void>();
const inFlightChanged = () => {
  for (const listener of [...inFlightListeners]) {
    listener();
  }
};

/** Holds automatic reloads while `source` is busy (until the returned release). */
export function addInFlightSource(source: InFlightSource): () => void {
  inFlightSources.set(source, source.subscribe(inFlightChanged));
  return () => {
    inFlightSources.get(source)?.();
    inFlightSources.delete(source);
    inFlightChanged();
  };
}

// API calls awaiting their response headers (`apiFetch`): the browser stores a Set-Cookie with
// the headers, so a call counts until they arrive, not while a stream's body is read.
let pendingRequests = 0;
const apiRequests: InFlightSource = {
  busy: () => pendingRequests > 0,
  subscribe: () => () => {},
};
addInFlightSource(apiRequests);

/** Counts `request` as in flight until it settles; returns it unchanged. */
export function trackRequest<T>(request: Promise<T>): Promise<T> {
  pendingRequests++;
  const done = () => {
    pendingRequests--;
    inFlightChanged();
  };
  request.then(done, done);
  return request;
}

/** The app's queries and mutations in flight hold the reload too (`BuildRefresh` registers). */
export function watchQueryClient(client: QueryClient): () => void {
  return addInFlightSource({
    busy: () => client.isFetching() > 0 || client.isMutating() > 0,
    subscribe: (listener) => {
      const queries = client.getQueryCache().subscribe(listener);
      const mutations = client.getMutationCache().subscribe(listener);
      return () => {
        queries();
        mutations();
      };
    },
  });
}

/**
 * Something in flight. `requestsOnly`: API calls awaiting their response only, for a required
 * reload (`client_outdated`), whose failing queries stay fetching through their retries.
 */
const inFlight = (requestsOnly = false) =>
  requestsOnly ? apiRequests.busy() : [...inFlightSources.keys()].some((source) => source.busy());

/** One wait per scope, shared by every reload attempt made meanwhile. */
const settling = new Map<boolean, Promise<boolean>>();

/**
 * Resolves true once nothing is in flight, checked again a task later (a settled query may start
 * the next one, a refetch burst lands over several ticks); false after `timeoutMs`.
 */
export function whenSettled(timeoutMs = SETTLE_MS, requestsOnly = false): Promise<boolean> {
  const shared = settling.get(requestsOnly);
  if (shared) {
    return shared;
  }
  const wait = new Promise<boolean>((resolve) => {
    let tick: ReturnType<typeof setTimeout> | undefined;
    const finish = (settled: boolean) => {
      clearTimeout(deadline);
      clearTimeout(tick);
      inFlightListeners.delete(check);
      cancels.delete(cancel);
      if (settling.get(requestsOnly) === wait) {
        settling.delete(requestsOnly);
      }
      resolve(settled);
    };
    const cancel = () => finish(false);
    function check() {
      clearTimeout(tick);
      if (!inFlight(requestsOnly)) {
        tick = setTimeout(() => {
          if (!inFlight(requestsOnly)) {
            finish(true);
          }
        }, 0);
      }
    }
    const deadline = setTimeout(() => finish(false), timeoutMs);
    cancels.add(cancel);
    inFlightListeners.add(check);
    check();
  });
  settling.set(requestsOnly, wait);
  return wait;
}

/**
 * Every modal of the app (Radix dialogs and sheets, alert dialogs, popovers) is one of these. One
 * that is closing (its exit animation, the mobile sidebar after a link) no longer holds.
 */
export const isDialogOpen = () =>
  document.querySelector(
    ':is([role="dialog"], [role="alertdialog"]):not([data-state="closed"])',
  ) !== null;

/**
 * Something an automatic reload would lose: a streaming reply, an unsent composer or editor,
 * an upload, an open dialog, a request in flight. Screens already declare their unsaved work for
 * the session-ended banner (`useUnsavedWork`), which counts here too. `requestsOnly` counts only
 * API calls awaiting their response as in flight (see `inFlight`).
 */
export function isReloadHeld({ requestsOnly = false } = {}): boolean {
  return (
    heldByWork() || inFlight(requestsOnly) || hasUnsavedWork() || isDialogOpen() || isEditing()
  );
}

/** Work under way that outlives a screen's first requests: a question or reply, an upload. */
const heldByWork = () => holds.size > 0 || [...holdChecks].some((check) => check());

/**
 * The person is typing in a field that holds text (an inline edit some screen does not declare
 * as unsaved work, a code being entered). An empty, focused composer holds nothing.
 */
function isEditing(): boolean {
  const active = document.activeElement;
  if (active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement) {
    return (
      !active.readOnly &&
      !active.disabled &&
      !NON_TEXT_INPUTS.has(active.type) &&
      active.value !== ""
    );
  }
  return active instanceof HTMLElement && active.isContentEditable;
}
const NON_TEXT_INPUTS = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

export type SafePoint = "navigate" | "visible" | "outdated" | "chunk";

/**
 * Reloads into the confirmed build if nothing is held, the tab is visible and this tab has not
 * reloaded by itself for that build yet. The guard is written before the reload; without storage
 * the tab never reloads by itself.
 */
export function trySafeReload(reason: SafePoint): boolean {
  const target = confirmed;
  if (target === null || reloadedFor === target || !storageUsable) {
    return false;
  }
  if (
    document.visibilityState === "hidden" ||
    isReloadHeld({ requestsOnly: reason === "outdated" })
  ) {
    return false;
  }
  try {
    buildRefreshIo.storage().setItem(RELOADED_FOR_KEY, target);
  } catch {
    storageUsable = false;
    return false;
  }
  reloadedFor = target;
  buildRefreshIo.reload();
  return true;
}

/**
 * {@link trySafeReload} once the requests in flight have settled (at most `SETTLE_MS`; still in
 * flight then, the reload waits for the next safe point). Work already under way (a reply) ends
 * the attempt at once: reloading when it ends would reload under the person reading its result.
 * `since`: the attempt began then; a cancel since (an unmount, a new start) ends it.
 */
function reloadWhenSettled(reason: SafePoint, since = attempts): Promise<boolean> {
  if (since !== attempts || confirmed === null || confirmed === reloadedFor || heldByWork()) {
    return Promise.resolve(false);
  }
  return track(
    whenSettled(SETTLE_MS, reason === "outdated").then(
      (settled) => settled && since === attempts && trySafeReload(reason),
    ),
  );
}

/** Resolves true a task later (the new route has rendered); false if cancelled meanwhile. */
function nextTask(): Promise<boolean> {
  return new Promise((resolve) => {
    const cancel = () => {
      clearTimeout(timer);
      cancels.delete(cancel);
      resolve(false);
    };
    const timer = setTimeout(() => {
      cancels.delete(cancel);
      resolve(true);
    }, 0);
    cancels.add(cancel);
  });
}

/**
 * A route change: tried once the new route has rendered and its first requests have settled, so
 * a screen closing on the way (the mobile sidebar's sheet) is taken into account. A screen that
 * opens with work (a new conversation's first reply) is no safe point: the next one is awaited.
 */
export function scheduleSafeReload(reason: SafePoint): void {
  if (confirmed !== null && confirmed !== reloadedFor) {
    const since = attempts;
    void track(nextTask().then((rendered) => rendered && reloadWhenSettled(reason, since)));
  }
}

/**
 * The URL changed (a navigation or back and forward). A safe point only for a new path, not a
 * query or hash change on the same page (a panel, a tab, a folder), and not for an entry that
 * hands state over (home's question): its screen has work to pick up.
 */
export function onRouteChange(): void {
  const path = window.location.pathname;
  const moved = path !== routePath;
  routePath = path;
  if (moved && (window.history.state ?? null) === null) {
    scheduleSafeReload("navigate");
  }
}

/**
 * The tab is visible again: an unconfirmed candidate may be checked again, then the reload, once
 * the refetch burst of a returning tab has settled (one of its responses may rotate the session).
 */
export function onBecameVisible(): Promise<void> {
  const since = attempts;
  return track(
    (async () => {
      await confirmCandidate();
      await reloadWhenSettled("visible", since);
    })(),
  );
}

export const useBuildRefresh = () => useSyncExternalStore(subscribe, () => state);

startBuildRefresh();
